import { Injectable } from '@angular/core';
import { EMPTY, forkJoin, from, fromEvent, of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, timeout } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { isObject } from 'lodash';

interface PostMessage {
  readonly type: string;
  readonly [key: string]: any;
}

export enum DriveServiceWorker {
  MS_DRIVE = 'drive-sync.worker.js',
  NEW_FIREBASE = 'new-firebase-messaging-sw.js'
}

@Injectable({
  providedIn: 'root'
})
export class ServiceWorkerService {
  postMessage(driveServiceWorker: DriveServiceWorker, message: PostMessage) {
    return this.getRegistration(driveServiceWorker).pipe(
      take(1),
      switchMap((registration) =>
        registration || driveServiceWorker !== DriveServiceWorker.MS_DRIVE
          ? of(registration.active)
          : this.reRegisterMsDrive()
      ),
      map((serviceWorker) => serviceWorker.postMessage(message))
    );
  }

  postMessageToAllRegistrations(message: PostMessage) {
    return this.getRegistrations().pipe(
      take(1),
      map((registrations) =>
        registrations
          .map((registration) => registration.active)
          .filter((sw) => isObject(sw))
          .forEach((sw) => sw.postMessage(message))
      )
    );
  }

  isSupported() {
    return 'serviceWorker' in navigator;
  }

  wakeUp() {
    this.postMessageToAllRegistrations({ type: 'sw-wake-up' }).subscribe();
  }

  register(
    driveServiceWorker: DriveServiceWorker,
    type: WorkerType,
    scope = `/${driveServiceWorker}`
  ): Promise<ServiceWorkerRegistration> {
    return navigator.serviceWorker.register(`/${driveServiceWorker}`, { scope, type });
  }

  unregisterAllRegistrations() {
    return this.getRegistrations().pipe(
      take(1),
      mergeMap((registrations) => forkJoin(registrations.map((registration) => registration.unregister())))
    );
  }

  private getRegistration(driveServiceWorker: DriveServiceWorker) {
    return this.getRegistrations().pipe(
      map((registrations) =>
        registrations.find(
          (registration) =>
            registration.active && this.getServiceWorkerSourceFileName(registration.active) === driveServiceWorker
        )
      )
    );
  }

  // drive-sync.worker.js is registered with type 'module' to obtain the ability to load ES modules
  registerMsDrive(): Promise<ServiceWorkerRegistration | undefined> {
    return this.register(DriveServiceWorker.MS_DRIVE, 'module', '/requests').then(
      (registration) => {
        console.debug('[drive-sync] ServiceWorker registration successful: ', registration);
        return registration;
      },
      (err) => {
        console.error('[drive-sync] ServiceWorker registration failed: ', err);
        throw err;
      }
    );
  }

  private reRegisterMsDrive() {
    console.warn('[drive-sync] ServiceWorker not active, (re-)registering');

    return from(this.registerMsDrive()).pipe(
      switchMap((registration) => {
        const serviceWorker = registration?.active ?? registration?.waiting ?? registration?.installing;
        if (!serviceWorker) {
          return throwError(() => new Error('[drive-sync] Registration returned no ServiceWorker'));
        }
        return serviceWorker.state === 'activated'
          ? of(serviceWorker)
          : fromEvent(serviceWorker, 'statechange').pipe(
              filter(() => serviceWorker.state === 'activated'),
              take(1),
              map(() => serviceWorker)
            );
      }),
      timeout(10_000),
      catchError((error) => {
        Sentry.captureException(error, { tags: { marker: 'drive-sync-reregister-failed' } });
        return throwError(() => new Error('error.service-worker.unavailable'));
      })
    );
  }

  getRegistrationsSourceFileName() {
    return this.getRegistrations().pipe(
        take(1),
        map((registrations) =>
          registrations
          .map((registration) => registration.active)
          .filter((sw) => isObject(sw))
          .map((sw) => this.getServiceWorkerSourceFileName(sw))
        )
    );
  }

  private getRegistrations() {
    if ('serviceWorker' in navigator) {
      return from(navigator.serviceWorker.getRegistrations());
    } else {
      this.logServiceWorkerInitialisationFailure();
      return EMPTY;
    }
  }

  private getServiceWorkerSourceFileName(serviceWorker: ServiceWorker) {
    return serviceWorker?.scriptURL.split('/').pop();
  }

  private logServiceWorkerInitialisationFailure() {
    const logMsg = `[SentryInitSW] Failed: Service worker unavailable`;
    console.debug(logMsg);
    Sentry.withScope((scope) => {
      scope.setTag('marker', 'SentryInitSW-failed');
      scope.setLevel('fatal');
      Sentry.captureMessage(logMsg, 'fatal');
    });
  }
}
